import { useWorkspaceText } from '../interface-copy';
import { useMemo, useState } from 'react';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import workspaceFeaturesCopy from '@/i18n/ns/workspace-features';
import { useWorkspace } from '../context';
import { useIntlLocale } from '../intl-locale';
import { WorkspaceIcon } from '../icons';
import { EmptyState, ErrorState, LoadingState, PageHeading } from '../primitives';
import { ProjectFaces, ProjectMark } from './mark';
import {
  endsLabel,
  lastActive,
  latestLine,
  personName,
  shortWhen,
  WHERE_ICON,
  whereLabel,
} from './labels';
import { openNewProject } from './new-project-request';
import { useProjects } from './use-projects';
import type { ProjectSummary } from './types';

type ProjectCopy = typeof workspaceFeaturesCopy.en;
const items = (count: number, copy: ProjectCopy) =>
  formatCopy(count === 1 ? copy.itemsOne : copy.itemsMany, { count });

/** One line per project: who can open it, how much is in it, and what happened last. */
function ProjectRow({ project }: { project: ProjectSummary }) {
  const ws = useWorkspace();
  const locale = useIntlLocale();
  const copy = useNamespaceCopy(workspaceFeaturesCopy);
  const latest = project.latest;
  return (
    <button
      type="button"
      className={`ws-projects-row ${project.finished ? 'finished' : ''}`}
      onClick={() => ws.openProject(project.id)}
    >
      <ProjectMark name={project.name} tint={project.tint} />
      <span className="ws-projects-row-text">
        <strong>{project.name}</strong>
        <small>
          {project.finished && project.endsOn ? (
            <>
              <span>{endsLabel(project.endsOn, true, locale)}</span>
              <span className="ws-projects-sep" aria-hidden="true">
                ·
              </span>
              <span>
                {formatCopy(copy.itemsAllStillHere, { items: items(project.itemCount, copy) })}
              </span>
            </>
          ) : (
            <>
              <span className="ws-projects-where">
                <WorkspaceIcon name={WHERE_ICON[project.visibility]} size={12} />
                {whereLabel(project.visibility, ws.companyName, locale)}
              </span>
              <span className="ws-projects-sep" aria-hidden="true">
                ·
              </span>
              <span>{items(project.itemCount, copy)}</span>
              {latest && (
                <>
                  <span className="ws-projects-sep" aria-hidden="true">
                    ·
                  </span>
                  <span className="ws-projects-latest">
                    {latest.live && <span className="ws-project-live" aria-hidden="true" />}
                    {latestLine(latest, ws.user?.id, locale)}
                  </span>
                </>
              )}
            </>
          )}
        </small>
      </span>
      <span className="ws-projects-row-people">
        {project.people.length > 1 && (
          <ProjectFaces
            people={project.people}
            max={3}
            label={project.people.map((person) => personName(person, locale)).join(', ')}
          />
        )}
      </span>
      <time dateTime={lastActive(project)}>
        {latest?.live ? copy.now : shortWhen(lastActive(project), locale)}
      </time>
    </button>
  );
}

/** All projects: the overview behind the sidebar's list, with search and the finished ones. */
export function ProjectsPage() {
  const { text: uiText } = useWorkspaceText();
  const ws = useWorkspace();
  const copy = useNamespaceCopy(workspaceFeaturesCopy);
  const { projects, loading, error, refresh } = useProjects(true, { revalidate: true });
  const [query, setQuery] = useState('');
  const shown = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase();
    const matching = needle
      ? projects.filter((project) =>
          `${project.name} ${project.summary}`.toLocaleLowerCase().includes(needle),
        )
      : projects;
    return [...matching].sort((a, b) => lastActive(b).localeCompare(lastActive(a)));
  }, [projects, query]);
  const active = shown.filter((project) => !project.finished);
  const finished = shown.filter((project) => project.finished);
  const nothing = !loading && !error && projects.length === 0;
  return (
    <div className="ws-projects">
      <PageHeading
        eyebrow={uiText('{company} workspace', { company: ws.companyName })}
        title={copy.projects}
        description={copy.projectsOverviewDescription}
      >
        <button className="ws-button primary" onClick={() => openNewProject()}>
          <WorkspaceIcon name="plus" size={16} />
          {copy.newProject}
        </button>
      </PageHeading>
      {nothing ? (
        <EmptyState
          icon="project"
          title={copy.firstProject}
          description={copy.firstProjectDescription}
        >
          <button className="ws-button primary" onClick={() => openNewProject()}>
            {copy.newProject}
            <WorkspaceIcon name="arrow" size={14} />
          </button>
        </EmptyState>
      ) : (
        <>
          <label className="ws-field-search ws-projects-search">
            <WorkspaceIcon name="search" size={16} />
            <input
              type="search"
              aria-label={copy.searchProjects}
              placeholder={copy.searchProjects}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </label>
          {error && <ErrorState message={error} onRetry={() => void refresh()} />}
          {loading && !projects.length && <LoadingState label={copy.loadingProjects} />}
          {!loading && query.trim() && !shown.length && (
            <p className="ws-page-note">{copy.noProjectMatchesHint}</p>
          )}
          {active.length > 0 && (
            <div className="ws-projects-list">
              {active.map((project) => (
                <ProjectRow key={project.id} project={project} />
              ))}
            </div>
          )}
          {finished.length > 0 && (
            <section aria-labelledby="projects-finished">
              <h2 className="ws-projects-group" id="projects-finished">
                {copy.finished}
              </h2>
              <div className="ws-projects-list">
                {finished.map((project) => (
                  <ProjectRow key={project.id} project={project} />
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
